import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import './AuditLogTable.css';

export type AuditActionType =
  | 'upload'
  | 'download'
  | 'edit'
  | 'delete'
  | 'share'
  | 'login'
  | 'logout'
  | 'create'
  | 'update'
  | 'submit'
  | 'sync'
  | 'backup'
  | 'notification';

export interface AuditLogEntry {
  /** Unique entry ID */
  id: string;
  /** Timestamp of the action */
  timestamp: Date | string;
  /** Action type — drives the visible action label and per-action styling. */
  action: AuditActionType;
  /**
   * Server-truth event type from ADR-002 taxonomy (e.g. "document.uploaded").
   * Surfaced as a data attribute for downstream icon refinement and analytics
   * filtering. Optional — entries from legacy code paths will not carry it.
   */
  event_type?: string;
  /** Category (Documents, Farm Data, Compliance, User Activity, System) */
  category: string;
  /** User who performed the action */
  user: {
    name: string;
    email?: string;
    /**
     * True when `name` is a raw id fallback because the server could not
     * resolve a display label (e.g. deleted user). Drives reduced-emphasis
     * styling per ADR-005.
     */
    unresolved?: boolean;
  };
  /** Brief description of the action */
  description: string;
  /** Optional entity that was affected */
  entity?: {
    type: string;
    name: string;
    href?: string;
    /**
     * True when `name` is a raw id fallback because the server could not
     * resolve a display label. Drives reduced-emphasis styling per ADR-005.
     */
    unresolved?: boolean;
  };
}

export interface AuditLogTableProps {
  /** Log entries to display */
  entries: AuditLogEntry[];
  /** Number of entries per page */
  pageSize?: number;
  /** Initial page (0-indexed) */
  initialPage?: number;
  /** Optional class name */
  className?: string;
  /** Empty state message */
  emptyMessage?: string;
}

/** Format timestamp for display */
function formatTimestamp(timestamp: Date | string): string {
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/** Action type labels */
const ACTION_LABELS: Record<AuditActionType, string> = {
  upload: 'Uploaded',
  download: 'Downloaded',
  edit: 'Edited',
  delete: 'Deleted',
  share: 'Shared',
  login: 'Logged in',
  logout: 'Logged out',
  create: 'Created',
  update: 'Updated',
  submit: 'Submitted',
  sync: 'Synced',
  backup: 'Backed up',
  notification: 'Notification',
};

/**
 * AuditLogTable - A clean, paginated table for displaying audit log entries.
 * Used in the History section of the application.
 */
export const AuditLogTable: React.FC<AuditLogTableProps> = ({
  entries,
  pageSize = 20,
  initialPage = 0,
  className = '',
  emptyMessage = 'No audit log entries found',
}) => {
  const [currentPage, setCurrentPage] = useState(initialPage);

  // Sort entries by timestamp descending
  const sortedEntries = useMemo(() => {
    return [...entries].sort((a, b) => {
      const dateA = typeof a.timestamp === 'string' ? new Date(a.timestamp) : a.timestamp;
      const dateB = typeof b.timestamp === 'string' ? new Date(b.timestamp) : b.timestamp;
      return dateB.getTime() - dateA.getTime();
    });
  }, [entries]);

  // Pagination
  const totalPages = Math.ceil(sortedEntries.length / pageSize);
  const startIndex = currentPage * pageSize;
  const endIndex = Math.min(startIndex + pageSize, sortedEntries.length);
  const currentEntries = sortedEntries.slice(startIndex, endIndex);

  const canGoPrev = currentPage > 0;
  const canGoNext = currentPage < totalPages - 1;

  const handlePrevPage = () => {
    if (canGoPrev) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (canGoNext) setCurrentPage(currentPage + 1);
  };

  if (entries.length === 0) {
    return (
      <div className={`audit-log-table ${className}`}>
        <div className="audit-log-table__empty">
          <Clock className="audit-log-table__empty-icon" />
          <p className="audit-log-table__empty-message">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`audit-log-table ${className}`}>
      <div className="audit-log-table__container">
        <table className="audit-log-table__table">
          <thead className="audit-log-table__thead">
            <tr>
              <th className="audit-log-table__th audit-log-table__th--timestamp">Time</th>
              <th className="audit-log-table__th audit-log-table__th--action">Action</th>
              <th className="audit-log-table__th audit-log-table__th--category">Category</th>
              <th className="audit-log-table__th audit-log-table__th--user">User</th>
              <th className="audit-log-table__th audit-log-table__th--description">Description</th>
            </tr>
          </thead>
          <tbody className="audit-log-table__tbody">
            {currentEntries.map((entry, index) => {
              const userCellClass = [
                'audit-log-table__td',
                'audit-log-table__td--user',
                entry.user.unresolved ? 'audit-log-table__td--user-unresolved' : '',
              ]
                .filter(Boolean)
                .join(' ');
              const entityClass = [
                'audit-log-table__entity',
                entry.entity?.unresolved ? 'audit-log-table__entity--unresolved' : '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <tr
                  key={entry.id}
                  className={`audit-log-table__row ${index % 2 === 0 ? 'audit-log-table__row--even' : 'audit-log-table__row--odd'}`}
                  data-event-type={entry.event_type}
                >
                  <td className="audit-log-table__td audit-log-table__td--timestamp">
                    {formatTimestamp(entry.timestamp)}
                  </td>
                  <td className="audit-log-table__td audit-log-table__td--action">
                    <span className={`audit-log-table__action audit-log-table__action--${entry.action}`}>
                      {ACTION_LABELS[entry.action]}
                    </span>
                  </td>
                  <td className="audit-log-table__td audit-log-table__td--category">
                    {entry.category}
                  </td>
                  <td className={userCellClass}>
                    {entry.user.name}
                  </td>
                  <td className="audit-log-table__td audit-log-table__td--description">
                    <span className="audit-log-table__description">
                      {entry.description}
                      {entry.entity && (
                        <>
                          {' '}
                          {entry.entity.href ? (
                            <a href={entry.entity.href} className="audit-log-table__entity-link">
                              {entry.entity.name}
                            </a>
                          ) : (
                            <span className={entityClass}>{entry.entity.name}</span>
                          )}
                        </>
                      )}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="audit-log-table__pagination">
        <span className="audit-log-table__pagination-info">
          {startIndex + 1}–{endIndex} of {sortedEntries.length}
        </span>
        <div className="audit-log-table__pagination-controls">
          <button
            className="audit-log-table__pagination-btn"
            onClick={handlePrevPage}
            disabled={!canGoPrev}
            aria-label="Previous page"
          >
            <ChevronLeft className="audit-log-table__pagination-icon" />
          </button>
          <button
            className="audit-log-table__pagination-btn"
            onClick={handleNextPage}
            disabled={!canGoNext}
            aria-label="Next page"
          >
            <ChevronRight className="audit-log-table__pagination-icon" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuditLogTable;
